<script lang="ts" setup>
import { defineProps, defineEmits, ref, computed } from "vue";
import InputModal from "../InputModal.vue";
import SlideImagesBlock from "./SlideImagesBlock.vue";
import CheckListBlock from "./CheckListBlock.vue";
import AddImageModal from "../AddImageModal.vue";
import { transformSlug } from "@/utils/transformSlug";
import CustomAccordion from "./CustomAccordion.vue";
import RepeatableInput from "./RepeatableInput.vue";

type Props = {
  input: any;
  childInputs: any;
  project: string | string[];
  phase: number;
  phaseDetails: any;
  step: any;
  selectedTermins: any;
  showDetails?: boolean;
};
type Emits = {
  (event: "refresh"): void;
};

const emits = defineEmits<Emits>();

const props = defineProps<Props>();

let repeatableGroupNames = computed(() => {
  if (props.input) {
    let groupNames = [];
    if (
      props.input.repeatableGroupDetails &&
      props.input.repeatableGroupDetails.length > 0 &&
      !props.input.isConditionallyRepetable
    ) {
      groupNames = props.input.repeatableGroupDetails.map((item: any) => {
        return {
          id: item.id,
          name: `${transformSlug(props.input.projectReference)}: ${item.name}`,
          image: item.image,
          order: item.order,
        };
      });

      return groupNames;
    } else if (props.input.isConditionallyRepetable) {
      groupNames = props.input.repeatableGroupDetails.map((item: any) => {
        return {
          id: item.id,
          name: item.name,
          image: null,
          order: item.order,
        };
      });

      return groupNames;
    } else {
      for (let index = 0; index < props.input.repeatCounter; index++) {
        groupNames.push({
          id: index + 1,
          name: `${transformSlug(props.input.projectReference)}: ${
            props.input.repeatableInputNoun
          } ${index + 1}`,
          order: index + 1,
        });
      }
      return groupNames;
    }
  }
  return [];
});

const isInputModalOpen = ref(false);
const isAddImageModalOpen = ref(false);
const selectedInput = ref<any>(null);
const selectedStep = ref<any>(null);
const selectedInputGroupIndex = ref<any>(null);
const selectedInputIndex = ref<any>(null);

const toggleModalHandler: any = (
  input: any,
  step: any,
  inputIndex?: number,
  groupIndex?: number
) => {
  selectedInput.value = input;
  selectedStep.value = step;
  isInputModalOpen.value = !isInputModalOpen.value;
  selectedInputGroupIndex.value = groupIndex;
  selectedInputIndex.value = inputIndex;
};

/** Resolve display label for select/static_select inside repeatable groups.
 *  Handles value being an option ID (number/string) OR the option text itself. */
function getSelectDisplayLabel(input: any): string {
  if (input?.value == null || input?.value === "") return "";
  const val = input.value;
  const opts = input.options;
  if (!opts || !Array.isArray(opts)) return String(val);
  // Try matching by id (numeric or string)
  const byId = opts.find(
    (opt: any) =>
      Number(opt.id) === Number(val) || String(opt.id) === String(val)
  );
  if (byId) return String(byId.option ?? byId.name ?? val);
  // Try matching by option text (backend sometimes stores the text as value)
  const byText = opts.find(
    (opt: any) =>
      String(opt.option ?? "").toLowerCase() === String(val).toLowerCase() ||
      String(opt.name ?? "").toLowerCase() === String(val).toLowerCase()
  );
  if (byText) return String(byText.option ?? byText.name ?? val);
  return String(val);
}
</script>
<template>
  <InputModal
    v-if="isInputModalOpen && selectedInput"
    :input="selectedInput"
    :step="selectedStep"
    :is-open="isInputModalOpen"
    :project="project"
    :phase="phase"
    :termins="phaseDetails.termins"
    :group-index="selectedInputGroupIndex"
    :input-index="selectedInputIndex"
    @refresh="() => emits('refresh')"
    @toggle-modal="
      () => {
        isInputModalOpen = false;
        selectedInput = null;
      }
    "
  />
  <AddImageModal
    v-if="isAddImageModalOpen && selectedInput"
    :steps="phaseDetails.steps"
    :termins="phaseDetails.termins"
    :is-open="isAddImageModalOpen"
    :project="project"
    :input="selectedInput"
    :step="selectedStep"
    :header-title="selectedInput ? selectedInput.name : ''"
    :phase="phase"
    :group-index="selectedInputGroupIndex"
    :input-index="selectedInputIndex"
    @toggle-modal="isAddImageModalOpen = false"
    @refresh="() => emits('refresh')"
  />
  <div>
    <div
      class="bg-[#E5E4E2] rounded-primary-radius"
      v-for="detail in repeatableGroupNames"
      :key="detail.id"
    >
      <CustomAccordion
        v-if="childInputs.some(
          (input: any) =>
            input.repeatableGroupIndex + 1 === detail.order &&
            ((input.value && input.value.length > 0) || input.finishedInputs > 0)
        )"
        :step-name="detail.name"
        :header-image="detail.image"
        accordion-header-color="#E5E4E2"
        :sticky-height="`${
          showDetails ? 'top-[383px]' : 'top-[180px]'
        } sticky z-[16]`"
      >
        <div class="flex flex-col gap-3 relative p-3 pt-0">
          <!-- CHECKLIST -->
          <div
            v-if="childInputs.some((input: any) => input.type === 'checkbox' && input.repeatableGroupIndex+1 === detail.order)"
            class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow"
          >
            <CheckListBlock
              :checklist="childInputs.filter((input: any) => input.type === 'checkbox' && input.repeatableGroupIndex+1 === detail.order)"
            />
          </div>
          <!-- IMAGE -->
          <div
            v-if="childInputs.some((input: any) => input.type === 'image' && input.repeatableGroupIndex+1 === detail.order && input.value?.length > 0)"
          >
            <div
              v-for="item in childInputs.filter((input: any) => input.type === 'image' && input.repeatableGroupIndex+1 === detail.order && input.value?.length > 0)"
              :key="item.id"
              class="flex flex-col gap-1"
            >
              <SlideImagesBlock
                :is-group="true"
                :show-details="showDetails"
                :selected-termins="selectedTermins"
                :header-class="`${
                  showDetails ? 'top-[363px]' : 'top-[163px]'
                } sticky z-[15]`"
                @add-image-modal-open="
                  () => {
                    selectedInput = item;
                    selectedStep = step;
                    isAddImageModalOpen = true;
                    selectedInputGroupIndex = detail.order - 1;
                  }
                "
                :input="item"
                :step="step"
                :repeatable-group-detail="detail"
                :steps="phaseDetails.steps"
                :refresh="() => emits('refresh')"
              />
            </div>
          </div>
          <!-- REPEATABLE INPUT -->
          <div
            v-if="childInputs.some((input: any) => input.type === 'repeatable_input' && input.finishedInputs > 0)"
            class="flex flex-col gap-3"
          >
            <div
              v-for="childInput in childInputs.filter((input: any) => input.type === 'repeatable_input' && input.finishedInputs > 0)"
              :key="childInput.id"
              class=""
            >
              <RepeatableInput
                :showDetails="showDetails"
                :selectedTermins="selectedTermins"
                :step="step"
                :input="childInput"
                :project="project"
                :phaseDetails="phaseDetails"
                :phase="phase"
                @refresh="() => emits('refresh')"
              />
            </div>
          </div>

          <!-- TEXT -->
          <div
            v-if="childInputs.some((input: any) => input.type === 'text' && input.repeatableGroupIndex+1 === detail.order && input.value)"
            class="flex flex-col gap-3"
          >
            <div
              v-for="item in childInputs.filter((input:any) => input.type === 'text' && input.repeatableGroupIndex+1 === detail.order && input.value)"
              :key="item.id"
              class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow flex flex-col gap-2"
            >
              <!-- Top side -->
              <div class="w-full flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="flex items-center gap-1">
                    <img
                      class="w-3 h-3 cursor-pointer"
                      src="@/assets/calendar-icon.svg"
                      alt="Calendar"
                    />

                    <p class="text-sm text-[#898790] foont-medium">
                      {{
                        item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString("de")
                          : ""
                      }}
                    </p>
                  </div>
                  <div class="flex items-center gap-1">
                    <img
                      class="w-3 h-3"
                      src="@/assets/gray-user.svg"
                      alt="User"
                    />
                    <p
                      v-for="(worker, index) in item.workers"
                      :key="index"
                      class="text-sm text-[#898790] foont-medium"
                    >
                      {{ worker.fullname
                      }}<span v-if="index < item.workers.length - 1">,</span>
                    </p>
                  </div>
                </div>
                <img
                  @click="
                    toggleModalHandler(item, step, null, detail.order - 1)
                  "
                  class="w-4 h-4 cursor-pointer"
                  src="@/assets/pencil-gray.svg"
                  alt="Pencil"
                />
              </div>
              <!-- Content -->
              <div class="mt-4">
                <p class="text-base font-normal text-">
                  {{ item.name }}
                </p>
                <div class="p-3 rounded-primary-radius bg-[#e5ecf6] mt-2">
                  <p class="text-base font-normal text-">
                    {{ item.value }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- NUMBER -->
          <div
            v-if="childInputs.some((input: any) => input.type === 'number' && input.repeatableGroupIndex+1 === detail.order && input.value)"
            class="flex gap-1"
          >
            <div
              v-for="item in childInputs.filter((input:any) => input.type === 'number' && input.repeatableGroupIndex+1 === detail.order && input.value)"
              :key="item.id"
              class="p-3 w-1/3 bg-white rounded-primary-radius shadow-primary-box-shadow"
            >
              <!-- Top side -->
              <div class="w-full flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="flex items-center gap-1">
                    <img
                      class="w-3 h-3 cursor-pointer"
                      src="@/assets/calendar-icon.svg"
                      alt="Calendar"
                    />

                    <p class="text-sm text-[#898790] foont-medium">
                      {{
                        item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString("de")
                          : ""
                      }}
                    </p>
                  </div>
                  <div class="flex items-center gap-1">
                    <img
                      class="w-3 h-3"
                      src="@/assets/gray-user.svg"
                      alt="User"
                    />
                    <p
                      v-for="(worker, index) in item.workers"
                      :key="index"
                      class="text-sm text-[#898790] font-medium"
                    >
                      {{ worker.fullname
                      }}<span v-if="index < item.workers.length - 1">,</span>
                    </p>
                  </div>
                </div>
                <img
                  @click="
                    toggleModalHandler(item, step, null, detail.order - 1)
                  "
                  class="w-4 h-4 cursor-pointer"
                  src="@/assets/pencil-gray.svg"
                  alt="Pencil"
                />
              </div>
              <!-- Content -->
              <div class="mt-4 flex gap-2">
                <p class="text-base font-normal grow">
                  {{ item.name }}
                </p>
                <div
                  class="px-3 py-[1px] rounded-primary-radius bg-[#e5ecf6] mt-2 h-fit w-fit"
                >
                  <p class="text-base font-normal h-fit w-fit">
                    {{ item.value }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- SELECT -->
          <div
            v-if="childInputs.some((input: any) => (input.type === 'select' || input.type === 'static_select') && input.repeatableGroupIndex+1 === detail.order && input.value)"
            class="flex flex-col gap-3"
          >
            <div
              v-for="item in childInputs.filter((input:any) => (input.type === 'select' || input.type === 'static_select') && input.repeatableGroupIndex+1 === detail.order && input.value)"
              :key="item.id"
              class="p-3 w-full bg-white rounded-primary-radius shadow-primary-box-shadow flex flex-col gap-2"
            >
              <!-- Top side -->
              <div class="w-full flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="flex items-center gap-1">
                    <img
                      class="w-3 h-3 cursor-pointer"
                      src="@/assets/calendar-icon.svg"
                      alt="Calendar"
                    />

                    <p class="text-sm text-[#898790] foont-medium">
                      {{
                        item.createdAt
                          ? new Date(item.createdAt).toLocaleDateString("de")
                          : ""
                      }}
                    </p>
                  </div>
                  <div class="flex items-center gap-1">
                    <img
                      class="w-3 h-3"
                      src="@/assets/gray-user.svg"
                      alt="User"
                    />
                    <p
                      v-for="(worker, index) in item.workers"
                      :key="index"
                      class="text-sm text-[#898790] foont-medium"
                    >
                      {{ worker.fullname
                      }}<span v-if="index < item.workers.length - 1">,</span>
                    </p>
                  </div>
                </div>
                <img
                  @click="
                    toggleModalHandler(item, step, null, detail.order - 1)
                  "
                  class="w-4 h-4 cursor-pointer"
                  src="@/assets/pencil-gray.svg"
                  alt="Pencil"
                />
              </div>
              <!-- Content -->
              <div class="mt-4">
                <p class="text-base font-normal text-">
                  {{ item.name }}
                </p>
                <div class="p-3 rounded-primary-radius bg-[#e5ecf6] mt-2">
                  <p class="text-base font-normal text-">
                    {{ getSelectDisplayLabel(item) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomAccordion>
    </div>
  </div>
</template>
